
import type { MessageCategory } from "@/types/chat";

interface CategoryKeywords {
  [key: string]: {
    primary: string[];
    secondary: string[];
    technical: string[];
    regional: string[];
  };
}

const CATEGORY_KEYWORDS: CategoryKeywords = {
  COMPRO_ORO: {
    primary: ['compro oro', 'compro', 'vendere oro', 'dove vendere', 'negozio oro', 'banco metalli'],
    secondary: ['migliore prezzo', 'valutazione gratuita', 'ritiro', 'acquisto', 'dealer', 'rivenditore'],
    technical: ['quotazione', 'asta', 'perizia', 'certificazione', 'banco dei pegni'],
    regional: ['vicino', 'zona', 'città', 'provincia', 'regione', 'locale', 'quartiere']
  },
  
  PREZZI_ORO: {
    primary: ['prezzo oro', 'quotazione oro', 'valore oro', 'costo oro', 'oro oggi'],
    secondary: ['24kt', '24 kt', '18kt', '18 kt', '22kt', '14kt', '9kt', 'carati', 'kt'],
    technical: ['fixing', 'lbma', 'london fixing', 'spot', 'grammo', 'oncia', 'troy'],
    regional: ['mercato italiano', 'borsa italiana', 'europa', 'londra', 'new york']
  },
  
  PREZZI_ARGENTO: {
    primary: ['prezzo argento', 'quotazione argento', 'valore argento', 'argento oggi'],
    secondary: ['925', 'sterling', '999', 'puro', 'fino', '800'],
    technical: ['comex', 'nymex', 'silver spot', 'grammo argento', 'oncia argento'],
    regional: ['mercato argento', 'europa argento', 'trading argento']
  },
  
  PREZZI_GENERALI: {
    primary: ['prezzi', 'quotazioni', 'mercato', 'andamento', 'trend'],
    secondary: ['metalli preziosi', 'oro e argento', 'precious metals', 'investimento'],
    technical: ['volatilità', 'spread', 'bid-ask', 'futures', 'commodities'],
    regional: ['mercati europei', 'mercati mondiali', 'borse internazionali']
  },
  
  VALUTAZIONE: {
    primary: ['valutazione', 'quanto vale', 'stima', 'calcolo', 'valore'],
    secondary: ['gioielli', 'anello', 'collana', 'bracciale', 'orecchini', 'monete'],
    technical: ['purezza', 'titolo', 'lega', 'peso', 'grammi', 'caratura'],
    regional: ['perito locale', 'stima gratuita', 'valutazione immediata']
  },
  
  LOCALIZZAZIONE: {
    primary: ['vicino a me', 'nella mia zona', 'qui vicino', 'dove', 'località'],
    secondary: ['milano', 'roma', 'napoli', 'torino', 'firenze', 'bologna', 'bari', 'catania'],
    technical: ['gps', 'posizione', 'coordinate', 'distanza', 'raggio'],
    regional: ['nord italia', 'sud italia', 'centro italia', 'sicilia', 'sardegna']
  }
};

const CONTEXTUAL_MODIFIERS = {
  urgency: ['subito', 'urgente', 'veloce', 'immediato', 'ora', 'adesso'],
  comparison: ['migliore', 'confronto', 'vs', 'differenza', 'meglio'],
  investment: ['investimento', 'investire', 'comprare oro', 'acquisto oro'],
  selling: ['vendere', 'vendita', 'liquidare', 'smobilizzare']
};

export function analyzeMessageContext(message: string): MessageCategory {
  const lowerMessage = message.toLowerCase();
  const words = lowerMessage.split(/\s+/);
  
  let categoryScores: Record<MessageCategory, number> = {
    'COMPRO_ORO': 0,
    'PREZZI_ORO': 0,
    'PREZZI_ARGENTO': 0,
    'PREZZI_GENERALI': 0,
    'VALUTAZIONE': 0,
    'LOCALIZZAZIONE': 0,
    'GENERALE': 0
  };

  // Analisi pesata per categoria
  Object.entries(CATEGORY_KEYWORDS).forEach(([category, keywords]) => {
    const cat = category as MessageCategory;
    
    // Punteggio per parole chiave primarie (peso 3)
    keywords.primary.forEach(keyword => {
      if (lowerMessage.includes(keyword)) {
        categoryScores[cat] += 3;
      }
    });
    
    // Punteggio per parole chiave secondarie (peso 2)
    keywords.secondary.forEach(keyword => {
      if (lowerMessage.includes(keyword)) {
        categoryScores[cat] += 2;
      }
    });
    
    // Punteggio per termini tecnici (peso 2.5)
    keywords.technical.forEach(keyword => {
      if (lowerMessage.includes(keyword)) {
        categoryScores[cat] += 2.5;
      }
    });
    
    // Punteggio per termini regionali (peso 1.5)
    keywords.regional.forEach(keyword => {
      if (lowerMessage.includes(keyword)) {
        categoryScores[cat] += 1.5;
      }
    });
  });

  // Bonus per modificatori contestuali
  Object.values(CONTEXTUAL_MODIFIERS).flat().forEach(modifier => {
    if (lowerMessage.includes(modifier)) {
      if (categoryScores['COMPRO_ORO'] > 0) categoryScores['COMPRO_ORO'] += 0.5;
      if (categoryScores['VALUTAZIONE'] > 0) categoryScores['VALUTAZIONE'] += 0.5;
    }
  });

  // Logica di combinazione intelligente
  if (categoryScores['PREZZI_ORO'] > 0 && categoryScores['LOCALIZZAZIONE'] > 0) {
    categoryScores['COMPRO_ORO'] += 1;
  }
  
  if (categoryScores['VALUTAZIONE'] > 0 && categoryScores['PREZZI_ORO'] > 0) {
    categoryScores['VALUTAZIONE'] += 1;
  }

  // Trova la categoria con il punteggio più alto
  const maxScore = Math.max(...Object.values(categoryScores));
  
  if (maxScore < 1) {
    return 'GENERALE';
  }
  
  const topCategory = Object.entries(categoryScores)
    .find(([_, score]) => score === maxScore)?.[0] as MessageCategory;
  
  return topCategory || 'GENERALE';
}

export function getMessageComplexity(message: string): 'simple' | 'medium' | 'complex' {
  const wordCount = message.split(/\s+/).length;
  const hasMultipleQuestions = (message.match(/\?/g) || []).length > 1;
  const hasTechnicalTerms = /\b(kt|carati|grammi|oncia|quotazione|fixing|lbma|comex)\b/i.test(message);
  
  if (wordCount > 20 || hasMultipleQuestions || hasTechnicalTerms) {
    return 'complex';
  } else if (wordCount > 10) {
    return 'medium';
  }
  return 'simple';
}

export function getCurrentTimeContext(): 'market_hours' | 'after_hours' | 'weekend' {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  
  // Weekend
  if (day === 0 || day === 6) {
    return 'weekend';
  }
  
  // Orari di mercato (9-17 UTC+1)
  if (hour >= 9 && hour <= 17) {
    return 'market_hours';
  }
  
  return 'after_hours';
}
